/**
 * Step 10 — Hazard Identification + auto manager alert.
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const HAZARDS = [
  "Asbestos Risk", "Electrical Hazard", "Structural Instability", "Biohazard",
  "Visible Mold", "Lead Paint Risk", "Gas Leak Risk", "Sewage Contamination", "Other",
];

export default function Step10Hazards({ workflow, requestId, onAdvance }) {
  const init = workflow?.step_10 || {};
  const [picked, setPicked] = useState(init.hazards || []);
  const [otherText, setOtherText] = useState(init.other_text || "");
  const [notes, setNotes] = useState(init.notes || "");
  const [saving, setSaving] = useState(false);

  const toggle = (h) => setPicked(p => p.includes(h) ? p.filter(x => x !== h) : [...p, h]);

  const save = async () => {
    if (picked.includes("Other") && !otherText.trim()) return toast.error("Specify the other hazard");
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/10/save`, {
        hazards: picked, other_text: otherText || null, notes: notes || null,
      });
      if (picked.length > 0) toast.warning("Hazards flagged — manager has been alerted");
      else toast.success("No hazards reported");
      onAdvance(11);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step-10-form">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-5 h-5 text-rose-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 10 · Hazard Identification</h2>
      </div>

      <Card><CardContent className="pt-4">
        <div className="grid grid-cols-2 gap-2">
          {HAZARDS.map(h => {
            const sel = picked.includes(h);
            return (
              <Button key={h} type="button" variant={sel ? "default" : "outline"}
                      onClick={() => toggle(h)}
                      className={`h-12 text-xs ${sel ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}
                      data-testid={`step-10-hazard-${h.toLowerCase().replace(/ /g, "-")}`}>
                {h}
              </Button>
            );
          })}
        </div>
        {picked.includes("Other") && (
          <Input value={otherText} onChange={e => setOtherText(e.target.value)}
                 placeholder="Describe the other hazard" className="h-11 mt-3"
                 data-testid="step-10-other-text" />
        )}
        {picked.length > 0 && (
          <div className="mt-3 p-2 bg-rose-50 border border-rose-200 rounded">
            <Badge className="bg-rose-100 text-rose-700 mb-2">⚠ Manager Auto-Alert</Badge>
            <p className="text-[11px] text-rose-700">
              A manager push notification + audit log entry will be created automatically when you save.
            </p>
          </div>
        )}
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full mt-3 p-2 text-xs border rounded h-20"
                  placeholder="Additional notes (optional)" data-testid="step-10-notes" />
      </CardContent></Card>

      <Button onClick={save} disabled={saving} className="w-full h-12 bg-zinc-900 text-white"
              data-testid="step-10-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save & Continue</>}
      </Button>
    </div>
  );
}
